import { SagaIterator } from 'redux-saga';
import {
  all,
  call,
  select,
  put,
  take,
  fork,
  takeLatest,
  cancel,
} from 'redux-saga/effects';
import _ from 'lodash';

import {
  GET_CHAT,
  SEND_MESSAGE,
  ChatSummary,
  ChatMessage,
  Sender,
  SendMessageAction,
  GET_CHAT_DETAILS,
  SYNC_CHAT_DETAILS,
  STOP_SYNC,
  ChatProps,
} from './types';
import { firestore } from '../../constants/firebase';
import { getUserState } from '../user/selector';
import { MCAFirebaseSaga } from '../root-saga';
import { FirestoreSnapshot } from '../meals/types';
import {
  setChat,
  getChat,
  getsyncChatDetails,
  getChatDetails,
} from './actions';
import { setLoading, setError } from '../app-globals/actions';
import { SYNC_NOTIFICATIONS } from '../notifications/types';
import { getNotifications } from '../notifications/actions';
import { USER_LOGOUT } from '../user/types';

function* getMessages(id: string, senderid: string): SagaIterator<ChatProps> {
  const firestoreGet = MCAFirebaseSaga.firestore.getCollection;
  const firestoreGetDocument = MCAFirebaseSaga.firestore.getDocument;

  const [snapshot, usersnapshot] = yield all([
    call(firestoreGet, `chat/${id}/messages`),
    call(firestoreGetDocument, `users/${senderid}`),
  ]);

  const message: ChatMessage[] = [];
  snapshot.forEach((item: FirestoreSnapshot) => {
    const body = {
      id: item.id,
      message: item.data().message,
      senderid: item.data().senderid,
      timeadded: item.data().timeadded.toDate(),
    };
    message.push(body);
  });
  const user = usersnapshot.data();
  const sender: Sender = {
    id: senderid,
    firstName: user.firstName,
    image: user.image,
  };
  const chats = {
    id,
    message: _.orderBy(message, ['timeadded'], 'asc'),
    sender: sender,
  };
  return chats;
}

function* fetchChats(): SagaIterator<void> {
  try {
    const { user } = yield select(getUserState);

    const firestoreGet = MCAFirebaseSaga.firestore.getCollection;
    const chatRef = firestore
      .collection('chat')
      .where('users', 'array-contains', user.userid);

    const snapshot = yield call(firestoreGet, chatRef);

    const chat: ChatSummary[] = [];
    snapshot.forEach((item: FirestoreSnapshot) => {
      const body = {
        id: item.id,
        senderid: _.find(item.data().users, i => i !== user.userid),
      };
      chat.push(body);
    });

    const fetchedData = yield all(
      chat.map(item => {
        return call(getMessages, item.id, item.senderid);
      })
    );

    const sortedData = _.orderBy(
      fetchedData,
      (c: ChatProps) => {
        const latestMessage = _.last(c.message);
        return latestMessage?.timeadded;
      },
      'desc'
    );

    yield put(setChat(sortedData));
    yield put(setLoading(false, ''));
  } catch (e) {
    yield put(setLoading(false, ''));
    yield put(setError(true, e));
  }
}

function* sendMessage({ chat }: SendMessageAction): SagaIterator<void> {
  try {
    const { user } = yield select(getUserState);
    const firestoreAddDocument = MCAFirebaseSaga.firestore.addDocument;

    let chatid = chat.id;

    if (chat.receiver) {
      const addNewMessage = yield call(firestoreAddDocument, '/chat', {
        users: [user.userid, chat.receiver],
      });
      chatid = addNewMessage.id;
      yield put(getChat());
      yield put(getChatDetails());
    }

    const body = {
      timeadded: new Date(),
      message: chat.message,
      senderid: user.userid,
    };
    const send = yield call(
      firestoreAddDocument,
      `chat/${chatid}/messages`,
      body
    );
    yield send;
  } catch (e) {}
}

function* syncChatDetails() {
  const { user } = yield select(getUserState);
  if (user) {
    yield put(getChat());
  }
}

function* syncChat(item: string) {
  yield fork(
    MCAFirebaseSaga.firestore.syncCollection,
    `chat/${item}/messages`,
    {
      successActionCreator: getsyncChatDetails,
    }
  );
}

function* syncChatDetailsSaga() {
  const { chat } = yield take('SET_CHAT');

  const collectionIds = chat.map((c: any) => c.id);

  const task = yield all(
    collectionIds.map((item: string) => {
      return call(syncChat, item);
    })
  );

  yield task;

  // Prevents inifinite loop
  yield take(STOP_SYNC);
  yield cancel(task);
}

function* syncNotification() {
  const { user } = yield select(getUserState);
  if (user.userid) {
    const task = yield fork(
      MCAFirebaseSaga.firestore.syncCollection,
      `notification/${user.userid}/notification`,
      {
        successActionCreator: getNotifications,
      }
    );

    yield task;

    yield take(USER_LOGOUT);
    yield cancel(task);
  }
}

export function* ChatSaga(): SagaIterator<void> {
  yield all([
    takeLatest(GET_CHAT, fetchChats),
    takeLatest(SEND_MESSAGE, sendMessage),
    takeLatest(GET_CHAT_DETAILS, syncChatDetailsSaga),
    takeLatest(SYNC_CHAT_DETAILS, syncChatDetails),
    takeLatest(SYNC_NOTIFICATIONS, syncNotification),
  ]);
}
